import { useState } from "react";
import { Plus, MessageCircle, Trash2, Edit2, Check, X } from "lucide-react";

const ConversationSidebar = ({ 
  conversations, 
  activeConversationId, 
  onSelectConversation, 
  onNewConversation,
  onDeleteConversation,
  onUpdateTitle,
  language,
  isOpen,
  onClose
}) => {
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");

  const t = (es, ca) => language === "ca" ? ca : es;

  const handleStartEdit = (conv) => {
    setEditingId(conv.id);
    setEditTitle(conv.title);
  };

  const handleSaveEdit = async (convId) => {
    if (editTitle.trim()) {
      await onUpdateTitle(convId, editTitle.trim());
    }
    setEditingId(null);
    setEditTitle("");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) return t("Hoy", "Avui");
    if (days === 1) return t("Ayer", "Ahir");
    if (days < 7) return t(`Hace ${days} días`, `Fa ${days} dies`);
    return date.toLocaleDateString(language === "ca" ? "ca-ES" : "es-ES", { day: "numeric", month: "short" });
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/60 z-40"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <div className={`
        fixed md:relative inset-y-0 left-0 z-50
        w-72 bg-slate-950/95 border-r border-slate-800
        transform transition-transform duration-200 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        flex flex-col h-full
      `}>
        {/* New conversation button */}
        <div className="p-3 border-b border-slate-800">
          <button
            data-testid="new-conversation-btn"
            onClick={onNewConversation}
            className="w-full flex items-center gap-2 px-4 py-3 rounded-xl bg-teal-500/20 hover:bg-teal-500/30 border border-teal-500/30 text-teal-300 transition-all"
          >
            <Plus className="w-5 h-5" />
            <span className="font-medium">{t("Nueva conversación", "Nova conversa")}</span>
          </button>
        </div>

        {/* Conversations list */}
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {conversations.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              {t("No hay conversaciones", "No hi ha converses")}
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                data-testid={`conversation-${conv.id}`}
                className={`
                  group flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-all
                  ${activeConversationId === conv.id 
                    ? 'bg-slate-800 border border-slate-700' 
                    : 'hover:bg-slate-900 border border-transparent'}
                `}
                onClick={() => editingId !== conv.id && onSelectConversation(conv.id)}
              >
                <MessageCircle className={`w-4 h-4 flex-shrink-0 ${activeConversationId === conv.id ? 'text-teal-400' : 'text-gray-500'}`} />
                
                {editingId === conv.id ? (
                  <div className="flex-1 flex items-center gap-1">
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveEdit(conv.id);
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      className="flex-1 bg-slate-700 border border-slate-600 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-teal-400"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSaveEdit(conv.id); }}
                      className="p-1 text-green-400 hover:text-green-300"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCancelEdit(); }}
                      className="p-1 text-gray-400 hover:text-gray-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${activeConversationId === conv.id ? 'text-white' : 'text-gray-300'}`}>
                        {conv.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {formatDate(conv.updated_at || conv.created_at)}
                      </p>
                    </div>
                    
                    {/* Actions (visible on hover or active) */}
                    <div className={`flex items-center gap-1 ${activeConversationId === conv.id ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleStartEdit(conv); }}
                        className="p-1 text-gray-400 hover:text-white transition-colors"
                        title={t("Editar título", "Editar títol")}
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); onDeleteConversation(conv.id); }}
                        className="p-1 text-gray-400 hover:text-red-400 transition-colors"
                        title={t("Eliminar", "Eliminar")}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        {/* Mobile close button */}
        <div className="md:hidden p-3 border-t border-slate-800">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            {t("Cerrar", "Tancar")}
          </button>
        </div>
      </div>
    </>
  );
};

export default ConversationSidebar;
